import { Atom, atom, getDefaultStore } from 'jotai'
import { atomWithStorage, unwrap } from 'jotai/utils'
import { currentPubkeyAtom } from './session'
import { favoriteRelaysWithSetsAtom, favoriteFollowListsWithSetsAtom } from './lists'
import { hideFollowingAtom } from './preferences'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { observe } from 'jotai-effect'
import { naddrEncode, npubEncode } from '@nostr/tools/nip19'

const store = getDefaultStore()

export type TFeed =
  | { pubkey: string }
  | { relay: string }
  | { relaySet: ResolvedSet<string> }
  | { followSet: ResolvedSet<string> }
  | { hardcodedFeed: number }

export const PRIMARY_QUERY_PARAMS = ['r', 'p', 'h', 's', 'search']

export function feedQueryParam(feed: TFeed): [string, string] | null {
  if ('relay' in feed) {
    return ['r', feed.relay.replace(/^wss?:\/\//, '').replace(/\/+$/, '')]
  }
  if ('pubkey' in feed) {
    return ['p', npubEncode(feed.pubkey)]
  }
  if ('hardcodedFeed' in feed) {
    return ['h', String(feed.hardcodedFeed)]
  }
  if ('relaySet' in feed) {
    return [
      's',
      naddrEncode({
        pubkey: feed.relaySet.pointer.pubkey,
        kind: feed.relaySet.pointer.kind,
        identifier: feed.relaySet.pointer.identifier,
        relays: feed.relaySet.items.slice(0, 3)
      })
    ]
  }
  return null
}

// persisted user override (null when nothing chosen yet)
export const storedFeedAtom = atomWithStorage<TFeed | null>('s:feedInfo', null, undefined, {
  getOnInit: true
})

export const feedAtom: Atom<Promise<TFeed | undefined>> = atom(async (get) => {
  const stored = get(storedFeedAtom)
  if (stored) return stored

  const hardcoded = window.hallway.universe.hardcodedFeeds
  if (hardcoded && hardcoded.length > 0) return { hardcodedFeed: 0 }

  const pubkey = get(currentPubkeyAtom)
  const hideFollowing = get(hideFollowingAtom)
  if (pubkey && !hideFollowing) return { pubkey }

  const favorites = await get(favoriteRelaysWithSetsAtom)
  if (!favorites || favorites.length === 0) return undefined

  const firstItem = favorites[0]
  if (typeof firstItem === 'string') return { relay: firstItem }

  return { relaySet: firstItem }
})
export const unwrappedFeedAtom = unwrap(feedAtom)

export const includeRepliesAtom = atomWithStorage<boolean>(
  's:includeReplies',
  window.hallway.universe.includeRepliesDefault,
  undefined,
  { getOnInit: true }
)

export const groupedAtom = atomWithStorage<boolean>(
  's:grouped',
  window.hallway.universe.groupedDefault,
  undefined,
  { getOnInit: true }
)

export const recentRelayFeedsAtom = atomWithStorage<(string | ResolvedSet<string>)[]>(
  's:recentRelayFeeds',
  [],
  undefined,
  { getOnInit: true }
)

// auto-update the feed when the follow sets update
observe((get, set) => {
  const pubkey = get.peek(currentPubkeyAtom)
  const feed = get.peek(feedAtom)
  const favoriteFollowSets = get(favoriteFollowListsWithSetsAtom)
  let cancelled = false

  Promise.all([feed, favoriteFollowSets]).then(([feed, favoriteFollowSets]) => {
    if (cancelled) return

    if (!feed || !('followSet' in feed) || feed.followSet.pointer.pubkey !== pubkey) return

    const current = store.get(storedFeedAtom)
    if (
      !current ||
      !('followSet' in current) ||
      current.followSet.pointer.pubkey !== feed.followSet.pointer.pubkey ||
      current.followSet.pointer.identifier !== feed.followSet.pointer.identifier
    ) {
      return
    }

    const selectedFollowSet = favoriteFollowSets.find(
      (s) =>
        s.pointer.identifier === feed.followSet.pointer.identifier &&
        s.pointer.pubkey === feed.followSet.pointer.pubkey
    )
    set(storedFeedAtom, selectedFollowSet ? { followSet: selectedFollowSet } : null)
  })

  return () => {
    cancelled = true
  }
})
